import React, { useState, useEffect } from 'react';
import { conversationService } from '../../services/api';

export default function WhatsappFlowMessage({ message }) {
  const [flowData, setFlowData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper function to check if this is a flow message
  const isFlowMessage = (message) => {
    if (message.message_content) {
      try {
        const content = JSON.parse(message.message_content);
        if (content.type === 'flow') {
          return true;
        }
      } catch (e) {
        // Not JSON, continue
      }
    }
    return false;
  };

  useEffect(() => {
    const fetchFlowData = async () => {
      try {
        let flowDataId = null;

        if (isFlowMessage(message)) {
          try {
            const content = JSON.parse(message.message_content);
            flowDataId = content.meta_whatsapp_flow_data_id;
          } catch (e) {
            console.error('Error parsing WhatsApp flow message content:', e);
          }
        }

        if (flowDataId && flowDataId !== 'unknown') {
          setLoading(true);
          setError(null);

          const data = await conversationService.getWhatsappFlowData(flowDataId);
          setFlowData(data);
        }
      } catch (err) {
        console.error('Error fetching WhatsApp flow data:', err);
        setError(err.message || 'Failed to fetch flow data');
      } finally {
        setLoading(false);
      }
    };

    fetchFlowData();
  }, [message]);

  // Check if this is a flow message using the helper function
  if (!isFlowMessage(message)) {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-2">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span className="text-blue-700 text-sm">Loading WhatsApp flow data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-2">
        <div className="flex items-center space-x-2">
          <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span className="text-red-700 text-sm">Error: {error}</span>
        </div>
      </div>
    );
  }

  if (!flowData) {
    // Show the raw message content while we don't have flow data
    let parsedContent = null;
    try {
      parsedContent = JSON.parse(message.message_content);
    } catch (e) {
      parsedContent = message.message_content;
    }

    return (
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 my-2">
        <div className="flex items-center space-x-2 mb-3">
          <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" clipRule="evenodd" />
          </svg>
          <span className="text-purple-800 font-medium">WhatsApp Flow Message</span>
        </div>

        <div className="space-y-2 text-sm">
          {typeof parsedContent === 'object' ? (
            <div className="space-y-2">
              {parsedContent.flow_name && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Flow Name</label>
                  <span className="text-sm text-gray-900">{parsedContent.flow_name}</span>
                </div>
              )}
              {parsedContent.meta_whatsapp_flow_data_id && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Flow Data ID</label>
                  <span className="text-sm text-gray-900 font-mono">{parsedContent.meta_whatsapp_flow_data_id}</span>
                </div>
              )}
              {parsedContent.type && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                  <span className="text-sm text-gray-900">{parsedContent.type}</span>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Raw Data</label>
                <div className="bg-white border border-gray-200 rounded p-2">
                  <pre className="text-xs text-gray-800 overflow-x-auto">
                    {JSON.stringify(parsedContent, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded p-2">
              <pre className="text-xs text-gray-800 overflow-x-auto">{parsedContent}</pre>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-green-50 border border-green-200 rounded-lg p-4 my-2">
      <div className="flex items-center space-x-2 mb-3">
        <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
        <span className="text-green-800 font-medium">WhatsApp Flow Data</span>
      </div>

      <div className="space-y-3">
        {/* Basic Flow Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Flow Name</label>
            <span className="text-sm text-gray-900">{flowData.flow_name || 'N/A'}</span>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Screen</label>
            <span className="text-sm text-gray-900">{flowData.screen || 'N/A'}</span>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Flow Version</label>
            <span className="text-sm text-gray-900">{flowData.flow_version || 'N/A'}</span>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Flow Token</label>
            <span className="text-sm text-gray-900 font-mono text-xs">
              {flowData.flow_token ? `${flowData.flow_token}` : 'N/A'}
            </span>
          </div>
        </div>

        {/* Data Payload */}
        {flowData.data_payload && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Data Payload</label>
            <div className="bg-white border border-gray-200 rounded p-2">
              <pre className="text-xs text-gray-800 overflow-x-auto">
                {JSON.stringify(flowData.data_payload, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {/* Response Data */}
        {flowData.response_data && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Response Data</label>
            <div className="bg-white border border-gray-200 rounded p-2">
              <pre className="text-xs text-gray-800 overflow-x-auto">
                {JSON.stringify(flowData.response_data, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {/* Error Message */}
        {flowData.error_message && (
          <div>
            <label className="block text-xs font-medium text-red-600 mb-1">Error Message</label>
            <div className="bg-red-50 border border-red-200 rounded p-2">
              <span className="text-sm text-red-800">{flowData.error_message}</span>
            </div>
          </div>
        )}

        {/* Timestamps */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-green-200">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Created</label>
            <span className="text-sm text-gray-900">
              {new Date(flowData.created_at).toLocaleString()}
            </span>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Updated</label>
            <span className="text-sm text-gray-900">
              {new Date(flowData.updated_at).toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
